/**
 * Считать ли эту отрисовку медленной загрузкой.
 *
 * `PerformanceEntry.startTime` у FCP и LCP отсчитывается от time origin — от
 * первой навигации во вкладке, а не от момента, когда человек начал ждать.
 * Вкладка, провисевшая в фоне или поднятая из bfcache, красится при возврате,
 * и startTime к этой секунде равен возрасту вкладки.
 *
 * На проде это давало значения до 836 344 092 мс — 9.7 суток — и средние по
 * дню в 12 000 секунд. Порог в 4 секунды срабатывал почти на каждой
 * долгоживущей вкладке, так что `slow_load` мерил возраст вкладки, а не
 * загрузку, и алертить по нему было нельзя.
 */

export const SLOW_LCP_MS = 4000;
export const SLOW_FCP_MS = 3000;

/**
 * Выше этого — уже не «медленно», а «вкладка висела». Две минуты с запасом
 * перекрывают даже самый безнадёжный мобильный интернет.
 */
export const MAX_PLAUSIBLE_LOAD_MS = 120_000;

export interface SlowLoadInput {
  metric: "lcp" | "fcp";
  /** `PerformanceEntry.startTime` как есть. */
  startTime: number;
  /** `PerformanceNavigationTiming.activationStart` — сдвиг для пререндера. */
  activationStart?: number;
  /** Вкладка уходила в фон до этой отрисовки. */
  pageEverHidden: boolean;
  /** Страница поднята из bfcache: краска мгновенная, загрузки не было. */
  restoredFromBfcache: boolean;
}

export type SlowLoadDecision =
  | { report: true; durationMs: number }
  | { report: false; reason: "bfcache" | "hidden" | "fast" | "implausible" };

export function decideSlowLoad(input: SlowLoadInput): SlowLoadDecision {
  if (input.restoredFromBfcache) return { report: false, reason: "bfcache" };
  if (input.pageEverHidden) return { report: false, reason: "hidden" };

  const durationMs = Math.round(input.startTime - (input.activationStart ?? 0));
  const threshold = input.metric === "lcp" ? SLOW_LCP_MS : SLOW_FCP_MS;

  if (durationMs <= threshold) return { report: false, reason: "fast" };
  if (durationMs > MAX_PLAUSIBLE_LOAD_MS) return { report: false, reason: "implausible" };

  return { report: true, durationMs };
}
